<div data-spartan-page class="p-6 md:p-8 space-y-6" style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page Header -->
  <div>
    <p class="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1">Shopee · Upload</p>
    <h1 class="text-2xl font-semibold tracking-tight text-foreground mb-1">Shopee Document Upload</h1>
    <p class="text-sm text-muted-foreground mt-1">Upload Shopee Excel and document files for processing</p>
  </div>

  <!-- Access denied -->
  @if (error && error.includes('Access denied')) {
    <div class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-500/15 dark:border-red-500/30 p-3 text-sm text-red-800 dark:text-red-300">
      <svg class="size-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25z"/></svg>
      <span><strong>Access Denied:</strong> {{ error }}</span>
    </div>
  }

  <!-- Success state -->
  @if (successMessage && !isUploading) {
    <div class="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 dark:bg-green-500/15 dark:border-green-500/30 p-3 text-sm text-green-800 dark:text-green-300">
      <svg class="size-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/></svg>
      <div>
        <p class="font-semibold mb-0.5">{{ successMessage }}</p>
        <p class="text-xs opacity-80">Redirecting to view imported data...</p>
      </div>
    </div>
  }

  <!-- Upload error (non-access) -->
  @if (error && !error.includes('Access denied')) {
    <div class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-500/15 dark:border-red-500/30 p-3 text-sm text-red-800 dark:text-red-300">
      <svg class="size-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z"/></svg>
      <span><strong>Upload Error:</strong> {{ error }}</span>
    </div>
  }

  @if (hasShopeeAccess$ | async) {

    <!-- Configuration Card -->
    <div class="rounded-lg border border-border bg-background">
      <div class="px-4 py-3 border-b border-border">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-sm font-semibold text-foreground">Upload Configuration</h2>
            @let currentRole = userRole$ | async;
            @if (currentRole) {
              <p class="text-xs text-muted-foreground mt-0.5">
                Logged in as:
                <span class="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium bg-green-50 text-green-700 border border-green-200 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/30 ml-1">
                  {{ currentRole | titlecase }}
                </span>
              </p>
            }
          </div>
        </div>
      </div>
      <div class="p-4">
        <form [formGroup]="uploadForm">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label class="block text-xs font-medium text-muted-foreground mb-1.5">Document Type</label>
              <div class="relative">
                <select formControlName="documentType" (change)="onDocumentTypeChange()"
                  class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none">
                  <option value="order">Shopee Orders</option>
                  <option value="shipment">Shipment Documents</option>
                  <option value="manifest">Customs Manifest</option>
                  <option value="customs">Customs Declaration</option>
                  <option value="lm">Pre-Alert Document</option>
                </select>
                <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
              </div>
            </div>
            <div>
              <label class="block text-xs font-medium text-muted-foreground mb-1.5">Processing Options</label>
              <div class="flex flex-col gap-2">
                <label class="flex items-center gap-2 cursor-pointer text-sm">
                  <input class="w-4 h-4 rounded border-border-strong" type="checkbox" formControlName="validateData" />
                  <span>Enable data validation</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer text-sm">
                  <input class="w-4 h-4 rounded border-border-strong" type="checkbox" formControlName="skipEmptyRows" />
                  <span>Skip empty rows</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer text-sm">
                  <input class="w-4 h-4 rounded border-border-strong" type="checkbox" formControlName="useApiUpload" />
                  <div>
                    <span class="font-medium">Use Backend API Processing</span>
                    <p class="text-xs text-muted-foreground mb-0">Upload file to server for processing</p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <!-- Advanced Options Toggle -->
          <button type="button"
            class="h-8 px-3 text-xs border border-border-strong bg-surface text-muted-foreground rounded-md hover:bg-surface-raised transition-colors mt-4"
            (click)="showAdvancedOptions = !showAdvancedOptions">
            {{ showAdvancedOptions ? 'Hide' : 'Show' }} Advanced Options
          </button>

          @if (showAdvancedOptions) {
            <div class="rounded-lg bg-surface-raised border border-border p-3 mt-3">
              <div class="grid grid-cols-1 gap-3">
                <label class="flex items-center gap-2 cursor-pointer text-sm">
                  <input class="w-4 h-4 rounded border-border-strong" type="checkbox" formControlName="strictValidation" />
                  <span>Strict validation (reject rows with any errors)</span>
                </label>
                <div>
                  <label class="block text-xs font-medium text-muted-foreground mb-1.5">Processing Notes</label>
                  <textarea formControlName="notes" rows="2"
                    placeholder="Optional notes about this upload..."
                    class="w-full border border-border-strong bg-surface rounded-md px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none transition resize-none"></textarea>
                </div>
              </div>
            </div>
          }
        </form>

        <!-- Required Columns Info -->
        <div class="flex items-start gap-3 rounded-lg border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/15 p-3 mt-4 text-sm text-blue-700 dark:text-blue-300">
          <svg class="size-4 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 24 24"><path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd"/></svg>
          <div>
            <span class="font-medium">Required Columns: </span>
            @for (column of config.requiredColumns; track column) {
              <span class="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30 mr-1">{{ column }}</span>
            }
          </div>
        </div>

        <div class="mt-3">
          <button type="button"
            class="h-9 px-3.5 text-sm border border-accent text-accent bg-blue-50 dark:bg-blue-500/15 rounded-md hover:bg-blue-100 dark:hover:bg-blue-500/25 transition-colors"
            (click)="downloadTemplate()">
            Download Template
          </button>
        </div>
      </div>
    </div>

    <!-- Upload Component Card -->
    @if (canUpload) {
      <div class="rounded-lg border border-border bg-background">
        <div class="p-4">
          <app-excel-upload
            [acceptedTypes]="'.xlsx,.xls'"
            [maxFileSize]="config.maxFileSize"
            [disabled]="!canUpload"
            (fileSelected)="onFileSelected($event)"
            (uploadProgress)="onUploadProgress($event)"
            (uploadComplete)="onUploadComplete($event)"
            (uploadError)="onUploadError($event)">
          </app-excel-upload>
        </div>
      </div>
    }

    <!-- Upload Progress Card -->
    @if (currentProgress && (isUploading || isPollingStatus)) {
      <div class="rounded-lg border border-border bg-background">
        <div class="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 class="text-sm font-semibold text-foreground">
            {{ useApiUpload ? 'Backend Processing' : 'Client Processing' }}
          </h2>
          @if (useApiUpload && canCancelApiJob) {
            <button type="button"
              class="h-7 px-2.5 text-[13px] border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-300 rounded-md hover:bg-red-100 transition-colors"
              (click)="cancelAPIJob()">
              Cancel
            </button>
          }
        </div>
        <div class="p-4">
          <div class="h-2 rounded-full overflow-hidden bg-surface-raised mb-2">
            <div class="h-2 rounded-full bg-accent transition-all"
              [style.width.%]="currentProgress.percentage">
            </div>
          </div>
          <div class="flex items-center justify-between text-sm">
            <span class="text-muted-foreground flex items-center gap-1.5">
              @if (currentProgress.stage !== 'complete') {
                <span class="inline-block h-3.5 w-3.5 rounded-full border-2 border-accent border-t-transparent animate-spin"></span>
              }
              {{ currentProgress.message }}
            </span>
            <span class="font-medium text-xs">{{ currentProgress.percentage }}%</span>
          </div>
          @if (useApiUpload && currentJob) {
            <div class="flex items-start gap-2 rounded-lg border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/15 p-2 mt-3 text-xs text-blue-700 dark:text-blue-300">
              <svg class="size-3.5 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 24 24"><path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd"/></svg>
              <span><strong>Status:</strong> {{ apiJobStatusText }}</span>
            </div>
          }
        </div>
      </div>
    }

    <!-- API Job Error -->
    @if (useApiUpload && currentJob && currentJob.status === 'error') {
      <div class="rounded-lg border border-red-200 dark:border-red-500/30 bg-background">
        <div class="px-4 py-3 border-b border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 rounded-t-lg flex items-center justify-between">
          <p class="font-semibold text-sm text-red-700 dark:text-red-300">Processing Failed</p>
          <button type="button"
            class="h-7 px-2.5 text-[13px] border border-red-200 dark:border-red-500/30 bg-background text-red-600 dark:text-red-300 rounded-md hover:bg-red-50 transition-colors"
            (click)="retryAPIJob()">
            Retry
          </button>
        </div>
        <div class="p-4 text-sm">
          <p class="mb-1 text-foreground">{{ currentJob.errorMessage || 'An error occurred during processing' }}</p>
          <p class="text-xs text-muted-foreground">Job ID: {{ currentJob.jobId }}</p>
        </div>
      </div>
    }

    <!-- API Job Completed -->
    @if (useApiUpload && currentJob && currentJob.status === 'completed') {
      <div class="rounded-lg border border-green-200 dark:border-green-500/30 bg-background">
        <div class="px-4 py-3 border-b border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/15 rounded-t-lg">
          <p class="font-semibold text-sm text-green-700 dark:text-green-300">Processing Complete</p>
        </div>
        <div class="p-4">
          <div class="grid grid-cols-4 gap-3 text-center mb-4">
            <div>
              <p class="font-semibold text-xl text-accent mb-0.5">{{ currentJob.totalRows || 0 }}</p>
              <p class="text-xs text-muted-foreground">Total Rows</p>
            </div>
            <div>
              <p class="font-semibold text-xl text-green-600 dark:text-green-400 mb-0.5">{{ currentJob.processedRows || 0 }}</p>
              <p class="text-xs text-muted-foreground">Processed</p>
            </div>
            <div>
              <p class="font-semibold text-xl text-amber-600 dark:text-amber-400 mb-0.5">{{ currentJob.warningRows || 0 }}</p>
              <p class="text-xs text-muted-foreground">Warnings</p>
            </div>
            <div>
              <p class="font-semibold text-xl text-red-600 dark:text-red-400 mb-0.5">{{ currentJob.errorRows || 0 }}</p>
              <p class="text-xs text-muted-foreground">Errors</p>
            </div>
          </div>
          <div class="flex items-center gap-2 flex-wrap">
            <button type="button"
              class="h-9 px-3.5 text-sm bg-accent text-accent-foreground border-0 rounded-md font-medium hover:bg-accent/90 transition-colors"
              (click)="downloadAPIResults()">
              Download Results
            </button>
            @if (currentJob.errorRows && currentJob.errorRows > 0) {
              <button type="button"
                class="h-9 px-3.5 text-sm border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 rounded-md hover:bg-amber-100 transition-colors"
                (click)="downloadAPIErrors()">
                Download Errors
              </button>
            }
            <button type="button"
              class="h-9 px-3.5 text-sm border border-border-strong bg-surface text-foreground rounded-md hover:bg-surface-raised transition-colors"
              (click)="resetUpload()">
              Upload Another
            </button>
          </div>
          <p class="mt-2 text-[11px] text-muted-foreground">
            Processing time: {{ getProcessingTime() }} | Success rate: {{ (currentJob.successRate || 0).toFixed(1) }}%
          </p>
        </div>
      </div>
    }

    <!-- Validation Summary (client-side) -->
    @if (hasResults && showValidationSummary && !useApiUpload) {
      <div class="rounded-lg border border-border bg-background">
        <div class="px-4 py-3 border-b border-border">
          <h2 class="text-sm font-semibold text-foreground">Validation Summary</h2>
        </div>
        <div class="p-4">
          <div class="mb-4">
            <div class="flex items-center justify-between text-sm mb-1">
              <span class="text-muted-foreground">Success Rate</span>
              <span class="font-semibold text-foreground">{{ processedResult!.summary.successRate.toFixed(1) }}%</span>
            </div>
            <div class="h-2 rounded-full overflow-hidden bg-surface-raised">
              <div class="h-2 rounded-full transition-all"
                [style.width.%]="processedResult!.summary.successRate"
                [style.background]="processedResult!.summary.successRate >= 80 ? '#16A34A' : processedResult!.summary.successRate >= 50 ? '#D97706' : '#DC2626'">
              </div>
            </div>
          </div>
          <div class="grid grid-cols-4 gap-3 text-center mb-4">
            <div>
              <p class="font-semibold text-xl text-accent mb-0.5">{{ processedResult!.summary.totalRows }}</p>
              <p class="text-xs text-muted-foreground">Total</p>
            </div>
            <div>
              <p class="font-semibold text-xl text-green-600 dark:text-green-400 mb-0.5">{{ processedResult!.validRowCount }}</p>
              <p class="text-xs text-muted-foreground">Valid</p>
            </div>
            <div>
              <p class="font-semibold text-xl text-amber-600 dark:text-amber-400 mb-0.5">{{ warningCount }}</p>
              <p class="text-xs text-muted-foreground">Warnings</p>
            </div>
            <div>
              <p class="font-semibold text-xl text-red-600 dark:text-red-400 mb-0.5">{{ errorCount }}</p>
              <p class="text-xs text-muted-foreground">Errors</p>
            </div>
          </div>
          @if (processedResult!.duplicateCount > 0) {
            <div class="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 p-2 mb-3 text-sm text-amber-700 dark:text-amber-300">
              <svg class="size-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z"/></svg>
              <span>{{ processedResult!.duplicateCount }} duplicate row(s) found and skipped.</span>
            </div>
          }
          <div class="flex items-center gap-2 flex-wrap">
            <button type="button"
              class="h-9 px-3.5 text-sm bg-accent text-accent-foreground border-0 rounded-md font-medium hover:bg-accent/90 transition-colors disabled:opacity-50"
              [disabled]="processedResult!.validRowCount === 0" (click)="exportValidData()">
              Export Valid ({{ processedResult!.validRowCount }})
            </button>
            <button type="button"
              class="h-9 px-3.5 text-sm border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 rounded-md hover:bg-amber-100 transition-colors disabled:opacity-50"
              [disabled]="!hasErrors" (click)="exportErrors()">
              Export Errors ({{ processedResult!.validationErrors.length }})
            </button>
            <button type="button"
              class="h-9 px-3.5 text-sm border border-border-strong bg-surface text-foreground rounded-md hover:bg-surface-raised transition-colors"
              (click)="resetUpload()">
              Upload New File
            </button>
          </div>
        </div>
      </div>
    }

    <!-- Error Details Table -->
    @if (hasErrors) {
      <div class="rounded-lg border border-border bg-background">
        <div class="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 class="text-sm font-semibold text-foreground">Validation Issues ({{ processedResult!.validationErrors.length }})</h2>
          <button type="button"
            class="h-7 px-2.5 text-[13px] border border-border-strong bg-surface text-muted-foreground rounded-md hover:bg-surface-raised transition-colors"
            (click)="showErrorDetails = !showErrorDetails">
            {{ showErrorDetails ? 'Hide' : 'Show' }} Details
          </button>
        </div>
        @if (showErrorDetails) {
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                  <th class="text-left px-4 py-2.5 font-medium">Row</th>
                  <th class="text-left px-4 py-2.5 font-medium">Column</th>
                  <th class="text-left px-4 py-2.5 font-medium">Value</th>
                  <th class="text-left px-4 py-2.5 font-medium">Issue</th>
                  <th class="text-left px-4 py-2.5 font-medium">Severity</th>
                </tr>
              </thead>
              <tbody>
                @for (err of processedResult!.validationErrors; track $index) {
                  <tr class="h-10 border-b border-border last:border-0 transition-colors"
                    [class.bg-red-50]="err.severity === 'error'"
                    [class.bg-amber-50]="err.severity === 'warning'">
                    <td class="px-4 text-muted-foreground text-sm">{{ err.row }}</td>
                    <td class="px-4 text-muted-foreground text-sm">{{ err.column }}</td>
                    <td class="px-4">
                      @if (err.value !== null && err.value !== undefined) {
                        <code class="font-mono text-xs bg-surface-raised px-1 rounded">{{ err.value }}</code>
                      } @else {
                        <span class="text-muted-foreground text-xs">(empty)</span>
                      }
                    </td>
                    <td class="px-4 text-muted-foreground text-sm">{{ err.message }}</td>
                    <td class="px-4">
                      <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium border"
                        [class.bg-red-50]="err.severity === 'error'"
                        [class.text-red-700]="err.severity === 'error'"
                        [class.border-red-200]="err.severity === 'error'"
                        [class.bg-amber-50]="err.severity === 'warning'"
                        [class.text-amber-700]="err.severity === 'warning'"
                        [class.border-amber-200]="err.severity === 'warning'">
                        {{ err.severity | titlecase }}
                      </span>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          @if (processedResult!.validationErrors.length > 100) {
            <div class="px-4 py-3 border-t border-border text-xs text-muted-foreground text-center">
              Showing first 100 errors. Download full error report for complete list.
            </div>
          }
        }
      </div>
    }

    <!-- Data Preview -->
    @if (hasResults && showPreview && processedResult!.validRowCount > 0) {
      <div class="rounded-lg border border-border bg-background">
        <div class="px-4 py-3 border-b border-border">
          <h2 class="text-sm font-semibold text-foreground">Data Preview (First {{ getPreviewCount() }} rows)</h2>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                @for (header of processedResult!.headers; track header) {
                  <th class="text-left px-4 py-2.5 font-medium">{{ header }}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of processedResult!.processedData.slice(0, maxPreviewRows); track $index) {
                <tr class="h-10 border-b border-border last:border-0 even:bg-surface-raised/50 hover:bg-surface-raised transition-colors">
                  @for (header of processedResult!.headers; track header) {
                    <td class="px-4 text-[12.5px] text-foreground">{{ row[header] }}</td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
        @if (processedResult!.validRowCount > maxPreviewRows) {
          <div class="px-4 py-3 border-t border-border text-xs text-muted-foreground text-center">
            ... and {{ processedResult!.validRowCount - maxPreviewRows }} more rows
          </div>
        }
      </div>
    }

    <!-- LM File Info Alert -->
    @if (config.documentType === 'lm' && !useApiUpload && !lmPreviewData && !isUploading) {
      <div class="flex items-start gap-3 rounded-lg border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/15 p-3 text-sm text-blue-700 dark:text-blue-300">
        <svg class="size-4 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 24 24"><path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd"/></svg>
        <span><strong>LM File Processing:</strong> After selecting your LM Excel file, you'll be prompted to enter pre-alert information before processing.</span>
      </div>
    }

    <!-- LM Preview Summary -->
    @if (lmPreviewData && !processedResult) {
      <div class="rounded-lg border border-border bg-background">
        <div class="px-4 py-3 border-b border-border bg-blue-50 dark:bg-blue-500/10 rounded-t-lg">
          <h2 class="text-sm font-semibold text-accent">LM File Preview Ready</h2>
        </div>
        <div class="p-4">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Filename</p>
              <p class="font-medium text-sm text-foreground">{{ lmPreviewData.filename }}</p>
            </div>
            <div>
              <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Total Rows</p>
              <p class="font-medium text-sm text-foreground">{{ lmPreviewData.totalRows }}</p>
            </div>
            <div>
              <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Status</p>
              <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium border"
                [class.bg-green-50]="lmPreviewData.validationPassed"
                [class.text-green-700]="lmPreviewData.validationPassed"
                [class.border-green-200]="lmPreviewData.validationPassed"
                [class.bg-red-50]="!lmPreviewData.validationPassed"
                [class.text-red-700]="!lmPreviewData.validationPassed"
                [class.border-red-200]="!lmPreviewData.validationPassed">
                {{ lmPreviewData.validationPassed ? 'Validation Passed' : 'Validation Failed' }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <button type="button"
              class="h-9 px-3.5 text-sm bg-accent text-accent-foreground border-0 rounded-md font-medium hover:bg-accent/90 transition-colors"
              (click)="showLMPreview()">
              Enter Pre-Alert Info
            </button>
            <button type="button"
              class="h-9 px-3.5 text-sm border border-border-strong bg-surface text-foreground rounded-md hover:bg-surface-raised transition-colors"
              (click)="resetUpload()">
              Cancel
            </button>
          </div>
        </div>
      </div>
    }

    <!-- LM Processed with Pre-Alert -->
    @if (processedResult && processedResult.requiresPreAlert) {
      <div class="rounded-lg border border-green-200 dark:border-green-500/30 bg-background">
        <div class="px-4 py-3 border-b border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/15 rounded-t-lg flex items-center justify-between">
          <p class="font-semibold text-sm text-green-700 dark:text-green-300">LM File Processed with Pre-Alert</p>
          <button type="button"
            class="h-7 px-2.5 text-[13px] border border-green-200 dark:border-green-500/30 bg-background text-green-600 dark:text-green-300 rounded-md hover:bg-green-50 transition-colors"
            (click)="editPreAlert()">
            Edit Pre-Alert
          </button>
        </div>
        <div class="p-4">
          <div class="flex items-start gap-3 rounded-lg border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/15 p-2 mb-3 text-sm text-blue-700 dark:text-blue-300">
            <svg class="size-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0 0 12 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z"/></svg>
            <span><strong>Pre-alert information has been captured and linked to this LM file.</strong> You can now export the processed data or upload another file.</span>
          </div>
          <div class="flex items-center gap-2">
            <button type="button"
              class="h-9 px-3.5 text-sm bg-accent text-accent-foreground border-0 rounded-md font-medium hover:bg-accent/90 transition-colors"
              (click)="exportValidData()">
              Export LM Data ({{ processedResult.validRowCount }})
            </button>
            <button type="button"
              class="h-9 px-3.5 text-sm border border-border-strong bg-surface text-foreground rounded-md hover:bg-surface-raised transition-colors"
              (click)="resetUpload()">
              Process Another File
            </button>
          </div>
        </div>
      </div>
    }

    <!-- Pre-Alert Input Modal (child component — preserved) -->
    <app-pre-alert-input-modal
      [isVisible]="showPreAlertModal"
      [isLoading]="isPreAlertLoading"
      [mode]="preAlertModalMode"
      [title]="preAlertModalTitle"
      [computedPieces]="lmPreviewData?.uniqueCartonCount || null"
      [filename]="lmPreviewData?.filename || selectedFile?.name || null"
      (modalResult)="onPreAlertModalResult($event)"
      (visibilityChange)="showPreAlertModal = $event">
    </app-pre-alert-input-modal>

  }

</div>
